import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api, fmtUSD, fmtPct, pdi, pdiTier } from "../lib/api";
import { Brain, Sparkle, Wrench, FlowArrow, GlobeHemisphereWest, TrendUp, TrendDown, UserCircleGear, PencilSimple, FloppyDisk, ArrowsClockwise } from "@phosphor-icons/react";

const TIER_ICON = {
  ORCHESTRATOR: Brain,
  SME:          Sparkle,
  UTILITY:      Wrench,
};

// Heritage-aligned card chrome — uniform crème + sand-border across all 3 tiers.
const TIER_CARD_CLS = "border border-[#E5E7EB] bg-white text-slate-900";
const TIER_LABEL = {
  ORCHESTRATOR: { name: "Strategic Partner Lead", icon: Brain },
  SME:          { name: "PDI Sentinel",             icon: Sparkle },
  UTILITY:      { name: "Data Hygiene Agent",       icon: Wrench },
};

export default function ProofModal({ open, onOpenChange, mode, target }) {
  const [proof, setProof] = useState(null);
  const [error, setError] = useState(null);
  const [refinement, setRefinement] = useState(null);

  useEffect(() => {
    if (!open || !target) return;
    setProof(null);
    setError(null);
    setRefinement(null);
    const call = mode === "SAVINGS"
      ? api.proofSavings(target.po_id)
      : api.proofRisk(target.supplier_id);
    call.then(setProof).catch((e) => setError(e?.message || "Proof unavailable"));
    // Pull any prior Strategic Lead refinement to surface as Human Professional Context
    if (mode === "SAVINGS" && target?.po_id) {
      api.getRefinement(target.po_id)
        .then((r) => setRefinement((r.refinements || [])[0] || null))
        .catch(() => setRefinement(null));
    }
  }, [open, mode, target]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="proof-modal"
        className="max-w-3xl bg-[#F9FAFB] border border-[#D1D5DB] text-slate-900 p-0 max-h-[90vh] overflow-hidden"
      >
        <DialogHeader className="px-6 py-4 border-b border-[#D1D5DB] bg-[#F3F4F6]">
          <div>
            <DialogTitle className="font-display text-lg flex items-center gap-2 text-slate-900">
              <FlowArrow weight="duotone" className="text-emerald-700" size={20} />
              Strategic Proof · Decision Intelligence
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-700 font-mono mt-1">
              {mode === "SAVINGS"
                ? `${target?.po_number} · ${target?.supplier}`
                : `${target?.supplier} · PDI ${pdi(target?.risk_score)} (${pdiTier(target?.risk_score)})`}
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="overflow-auto thin-scroll max-h-[calc(90vh-80px)]">
          {error && (
            <div className="p-6 text-rose-700 text-sm" data-testid="proof-error">
              <div className="font-medium mb-1">Proof generation failed.</div>
              <div className="text-xs text-slate-700 font-mono">{error}</div>
            </div>
          )}

          {!proof && !error && <ProofSkeleton />}

          {proof && (
            <div className="p-5 space-y-3.5">
              {/* ① Human Professional Context — Strategic Lead refinement (if any) */}
              <HumanContext refinement={refinement} mode={mode} />

              {/* ② Decision-Adjusted Calculation (SAVINGS only) */}
              {mode === "SAVINGS" && proof.risk_adjustment && (
                <RiskAdjustmentCard ra={proof.risk_adjustment} />
              )}

              {/* ③ Market Signal Linkage (SAVINGS only) */}
              {mode === "SAVINGS" && proof.market_linkage && (
                <MarketLinkageCard m={proof.market_linkage} />
              )}

              {/* ④ Strategic Partner reasoning summary */}
              <div
                data-testid="proof-summary"
                className="border border-[#E5E7EB] bg-white rounded-sm p-4"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Brain weight="duotone" className="text-slate-800" size={18} />
                  <span className="tracking-mono-label text-[11px] text-slate-700 font-semibold">
                    Strategic Partner Lead · Intelligence Trace
                  </span>
                </div>
                <p className="text-slate-950 leading-relaxed text-sm font-medium">
                  {humaniseSummary(proof.summary, proof.risk_adjustment) || "—"}
                </p>
              </div>

              {/* ⑤ Procureo Strategic Intelligence Network — clean agent army */}
              <div data-testid="proof-tiers" className="space-y-2">
                <div className="tracking-mono-label text-[11px] text-slate-700 font-semibold">
                  Procureo Strategic Intelligence Network · agent trace
                </div>
                {(["UTILITY", "SME", "ORCHESTRATOR"]).map((tierKey) => {
                  const t = proof.tiers?.[tierKey.toLowerCase()];
                  if (!t) return null;
                  const meta = TIER_LABEL[tierKey];
                  const Icon = meta.icon;
                  return (
                    <div
                      key={tierKey}
                      className={`${TIER_CARD_CLS} rounded-sm p-3.5`}
                      data-testid={`tier-card-${tierKey.toLowerCase()}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Icon weight="duotone" className="text-slate-800" size={16} />
                        <span className="font-display text-[13px] font-bold text-slate-900">
                          {meta.name}
                        </span>
                      </div>
                      <TierOutput output={t.output} />
                    </div>
                  );
                })}
              </div>

              {/* ⑥ Reasoning steps */}
              {proof.steps?.length > 0 && (
                <div data-testid="proof-steps" className="border border-[#E5E7EB] bg-white rounded-sm p-4">
                  <div className="tracking-mono-label text-[11px] text-slate-700 font-semibold mb-2">Reasoning Steps</div>
                  <ol className="space-y-1.5 text-sm text-slate-950 font-medium">
                    {proof.steps.map((s, i) => (
                      <li key={i} className="flex gap-2 leading-relaxed">
                        <span className="font-mono text-slate-700 shrink-0 font-bold">{String(i + 1).padStart(2, "0")}.</span>
                        <span>{humaniseStep(s, proof.risk_adjustment)}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {/* ⑦ Evidence */}
              {proof.evidence?.length > 0 && (
                <div data-testid="proof-evidence" className="border border-[#E5E7EB] bg-white rounded-sm p-4">
                  <div className="tracking-mono-label text-[11px] text-slate-700 font-semibold mb-2">Evidence</div>
                  <ul className="space-y-1.5">
                    {proof.evidence.map((e, i) => (
                      <li key={i} className="border-l-2 border-slate-400 pl-3 py-0">
                        <div className="text-[10px] font-mono text-slate-700 tracking-wide font-semibold">{prettifySource(e.source)}</div>
                        <div className="text-sm text-slate-950 leading-relaxed font-medium">{humaniseSignal(e.signal, proof.risk_adjustment)}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* ⑧ Editable Action Recommendation — Strategic Lead can refine + commit straight from the proof */}
              {mode === "SAVINGS" && target?.po_id && (
                <ProofRefinementEditor
                  poId={target.po_id}
                  poNumber={target.po_number}
                  proposedDefault={
                    refinement?.proposed_strategy ||
                    humaniseSummary(proof.summary, proof.risk_adjustment) ||
                    "Open the Maneuver Modal to generate a Strategic Partner draft, then refine here."
                  }
                  initialRefinement={refinement?.refined_strategy}
                  committedAt={refinement?.committed_at}
                  onCommitted={(updated) => setRefinement(updated)}
                />
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, accent = "slate" }) {
  const color = {
    slate: "text-slate-100",
    emerald: "text-emerald-400",
    rose: "text-rose-400",
  }[accent];
  return (
    <div className="bg-slate-950 p-3">
      <div className="tracking-mono-label text-[10px] text-slate-500">{label}</div>
      <div className={`font-mono text-base ${color} mt-1`}>{value}</div>
    </div>
  );
}

function RiskAdjustmentCard({ ra }) {
  const gross = Number(ra.gross_savings ?? 0);
  const score = Number(ra.risk_score ?? 0);
  const pdiVal = 100 - score;
  const tier   = pdiTier(score);
  const mult   = Number(ra.multiplier ?? pdiVal / 100);
  const adj    = Number(ra.risk_adjusted_savings ?? gross * mult);
  return (
    <div
      data-testid="risk-adjustment-card"
      className="border border-[#E5E7EB] bg-[#F9FAFB] rounded-sm p-4"
    >
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Brain weight="duotone" className="text-slate-800" size={18} />
          <span className="tracking-mono-label text-[11px] text-slate-700 font-semibold">
            Decision-Adjusted Calculation · 100 = MAX INTEGRITY
          </span>
        </div>
        <span
          data-testid="proof-pdi-stamp"
          className={`tier-${tier} px-3 py-1 text-sm tracking-mono-label font-extrabold tabular-nums whitespace-nowrap`}
          title={`PDI ${pdiVal} · ${tier} · Procurement Decision Intelligence`}
        >
          {pdiVal}
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_24px_1fr_24px_1.3fr] gap-2 items-end">
        <Formula label="Gross Savings"            value={fmtUSD(gross)} />
        <Operator op="×" />
        <Formula label={`PDI ${pdiVal}`}          value={mult.toFixed(2)} />
        <Operator op="=" />
        <Formula label="Decision-Adjusted"        value={fmtUSD(adj)} accent="emerald" />
      </div>
      {ra.rationale && (
        <p className="mt-3 text-sm text-slate-950 leading-relaxed border-t border-[#E5E7EB] pt-3 font-medium">
          <span className="text-slate-800 font-mono text-[10px] tracking-mono-label font-bold">RATIONALE · </span>
          {humaniseText(ra.rationale, ra)}
        </p>
      )}
    </div>
  );
}

function Formula({ label, value, accent = "slate" }) {
  const color = accent === "emerald" ? "text-emerald-800 font-bold" : "text-slate-900 font-semibold";
  return (
    <div className="bg-white border border-[#D1D5DB] rounded-sm p-2.5 min-w-0">
      <div className="tracking-mono-label text-[10px] text-slate-700 whitespace-nowrap font-semibold">{label}</div>
      <div className={`font-mono text-sm ${color} mt-0.5 whitespace-nowrap`}>{value}</div>
    </div>
  );
}

function Operator({ op }) {
  return <div className="font-mono text-xl text-slate-700 text-center select-none font-bold">{op}</div>;
}

// ─── Professional Strategy Review (Strategic Lead refinement surface) ───────
function HumanContext({ refinement, mode }) {
  const has = refinement?.refined_strategy;
  return (
    <div
      data-testid="proof-human-context"
      className="border border-[#E5E7EB] bg-white rounded-sm p-4"
    >
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-2">
          <UserCircleGear weight="duotone" className="text-slate-900" size={20} />
          <span className="font-display text-sm font-bold text-slate-950">
            Professional Strategy Review
          </span>
        </div>
        {has && refinement?.committed_at && (
          <span className="font-mono text-[10px] text-slate-800 tracking-mono-label font-semibold">
            committed {new Date(refinement.committed_at).toLocaleString()}
          </span>
        )}
      </div>
      {has ? (
        <div className="border-l-2 border-emerald-700 pl-3 py-1">
          <div className="font-mono text-[10px] text-emerald-800 tracking-mono-label mb-1 inline-flex items-center gap-1 font-semibold">
            <PencilSimple size={11} weight="bold" /> Strategic Lead Refinement
          </div>
          <p className="text-sm text-slate-950 leading-relaxed whitespace-pre-wrap font-medium">
            {refinement.refined_strategy}
          </p>
        </div>
      ) : (
        <p className="text-sm text-slate-800 italic font-medium">
          {mode === "SAVINGS"
            ? "Awaiting Professional Review and Strategic Playbook Commitment."
            : "Awaiting Professional Review and Strategic Playbook Commitment for this counterparty."}
        </p>
      )}
    </div>
  );
}

// ─── Tier output renderer · translates JSON payloads into bullets ───────────
function TierOutput({ output }) {
  if (output == null) return <span className="text-sm text-slate-600 italic">no output</span>;
  if (typeof output === "string") return <p className="text-sm text-slate-900 leading-relaxed">{humaniseSignal(output)}</p>;
  if (Array.isArray(output)) {
    return (
      <ul className="text-sm text-slate-900 space-y-1">
        {output.map((v, i) => (
          <li key={i} className="flex gap-2 leading-relaxed">
            <span className="text-slate-500">·</span>
            <span>{humaniseSignal(typeof v === "object" ? Object.values(v).join(" · ") : String(v))}</span>
          </li>
        ))}
      </ul>
    );
  }
  // Object → key/value rows, scrubbed for residual "risk" wording
  const rows = Object.entries(output).filter(([, v]) => v != null && v !== "");
  return (
    <ul className="text-sm text-slate-900 space-y-1">
      {rows.map(([k, v]) => (
        <li key={k} className="grid grid-cols-12 gap-2 leading-relaxed">
          <span className="col-span-4 font-mono text-[11px] text-slate-700 tracking-mono-label truncate font-semibold">
            {prettyKey(k)}
          </span>
          <span className="col-span-8 text-slate-900 break-words">
            {formatValue(k, v)}
          </span>
        </li>
      ))}
    </ul>
  );
}

const KEY_LABEL = {
  normalized:      "Normalized Entity",
  canonical:       "Canonical Form",
  entities:        "Entities Resolved",
  filings:         "Filings Reviewed",
  unspsc:          "UNSPSC Segment",
  segment:         "Segment",
  risk_score:      "PDI Score",
  risk_tier:       "PDI Tier",
  pdi:             "PDI Score",
  pdi_tier:        "PDI Tier",
  rationale:       "Rationale",
  action:          "Action",
  leverage_points: "Leverage Points",
  verdict:         "Verdict",
  confidence:      "Confidence",
  basis:           "Basis",
  signals:         "Signals",
  drivers:         "Drivers",
};
function prettyKey(k) { return KEY_LABEL[k] || k.replace(/_/g, " "); }
function formatValue(k, v) {
  // Translate risk_score & risk_tier into PDI presentation
  if (k === "risk_score" && typeof v === "number") return `${100 - v}/100 PDI`;
  if (k === "risk_tier") return `${v} PDI tier`;
  if (Array.isArray(v))   return humaniseSignal(v.map((x) => (typeof x === "object" ? Object.values(x).join(" · ") : x)).join(" · "));
  if (typeof v === "object") return humaniseSignal(Object.values(v).map((x) => String(x)).join(" · "));
  return humaniseSignal(String(v));
}

// ─── Humanise the orchestrator summary (legacy "risk score" wording) ────────
function humaniseSummary(text, ra) {
  return humaniseText(text, ra);
}

// ─── Source-name prettifier · maps internal tier slugs → professional agents
const SOURCE_REPLACEMENTS = [
  [/^\s*SME\s+Filing\s+Analysis\s*$/i,   "PDI Sentinel · Filing Review"],
  [/^\s*SME\s+UNSPSC\s+Mapping\s*$/i,    "PDI Sentinel · Category Mapping"],
  [/^\s*SME\s+(.+)$/i,                   (_, rest) => `PDI Sentinel · ${rest}`],
  [/^\s*Utility\s+Tier\s*$/i,            "Data Hygiene Agent"],
  [/^\s*Utility\s*$/i,                   "Data Hygiene Agent"],
  [/^\s*PO\s+Metadata\s*$/i,             "Contract Intelligence"],
  [/^\s*Orchestrator\s*$/i,              "Strategic Partner Lead"],
];
function prettifySource(source) {
  if (!source) return source;
  for (const [pat, repl] of SOURCE_REPLACEMENTS) {
    if (pat.test(source)) {
      return typeof repl === "function" ? source.replace(pat, repl) : repl;
    }
  }
  return source;
}

// ─── Signal humaniser · turns underscored flag-speak into prose ─────────────
const SIGNAL_PHRASES = [
  // Strip embedded source-tier prefixes that survive join() calls
  [/\bSME\s+Filing\s+Analysis\b\s*[·\-—]?\s*/gi,            ""],
  [/\bSME\s+UNSPSC\s+Mapping\b\s*[·\-—]?\s*/gi,             ""],
  [/\bUtility\s+Tier\b\s*[·\-—]?\s*/gi,                     ""],
  [/\bPO\s+Metadata\b\s*[·\-—]?\s*/gi,                      ""],
  // Filing / SEC flags
  [/NO[_\s]PUBLIC[_\s]FILINGS\s*flag\s*[—–-]\s*/gi,         "Public financial disclosure is unavailable for this entity — "],
  [/NO[_\s]PUBLIC[_\s]FILINGS\s*flag/gi,                    "Public financial disclosure is unavailable"],
  [/\bNO[_\s]PUBLIC[_\s]FILINGS\b/g,                        "no public filings"],
  // Subsidiary / parent flags – cover comma-list form
  [/SUBSIDIARY[_\s]ENTITY\s+and\s+PARENT[_\s]FILER[_\s]ONLY\s*flags\s*[—–-]\s*/gi,
                                                              "Counterparty operates as a subsidiary whose parent is the registered filer — "],
  [/SUBSIDIARY[_\s]ENTITY,?\s+(?:and\s+)?PARENT[_\s]FILER[_\s]ONLY,?\s*(?:and|,)?\s*PARENT[_\s]FILER[_\s]ONLY[_\s]flags/gi,
                                                              "subsidiary-only / parent-filer-only disclosure pattern"],
  [/\bSUBSIDIARY[_\s]ENTITY\b/g,                            "subsidiary contracting entity"],
  [/\bPARENT[_\s]FILER[_\s]ONLY\b/g,                        "parent-only filer"],
  // FOCI
  [/FOCI\s*risk\s*identified\s*[—–-]\s*/gi,                 "Foreign Ownership, Control, or Influence review is triggered — "],
  [/FOCI\s*risk/gi,                                          "Foreign Ownership, Control, or Influence (FOCI) review"],
  [/\bFOCI\b/g,                                              "FOCI"], // keep acronym; OK for finance audience
  // SAM.gov
  [/SAM\.gov\s*exclusion\s*status\s*could\s*not\s*be\s*independently\s*verified/gi,
                                                              "SAM.gov registration status could not be independently verified"],
  // Generic flag suffix
  [/\s+flags\b/gi,                                          ""],
  [/\s+flag\b/gi,                                           ""],
];
function humaniseSignal(text, ra) {
  if (!text) return text;
  let out = String(text);
  for (const [pat, repl] of SIGNAL_PHRASES) out = out.replace(pat, repl);
  // Final sweep for bare uppercase technical tokens that slipped through
  out = out
    .replace(/\b[A-Z]{2,}(?:_[A-Z0-9]+){1,}\b/g, (m) => m.toLowerCase().replace(/_/g, " "));
  return humaniseText(out, ra);
}

// ─── Step humaniser · strips [ACTOR] prefixes + applies PDI + flag scrub ───
function humaniseStep(text, ra) {
  if (!text) return text;
  let out = String(text)
    .replace(/^\s*\[ORCHESTRATOR\]\s*/i, "")
    .replace(/^\s*\[SME\]\s*/i,          "")
    .replace(/^\s*\[UTILITY\]\s*/i,      "");
  // Run through SIGNAL phrases so underscored flags & technical sources are removed
  return humaniseSignal(out, ra);
}

// ─── Master text scrub · PDI enforcement (zero "risk" wording allowed) ─────
function humaniseText(text, ra) {
  if (!text) return text;
  let out = String(text);
  const score   = Number(ra?.risk_score ?? NaN);
  const hasScore = Number.isFinite(score);
  const pdiVal  = hasScore ? 100 - score : null;
  const tier    = hasScore ? pdiTier(score) : null;

  // Numeric "risk score N/100 (TIER ...)" → "(100-N)/100 TIER PDI"
  // Keep trailing whitespace OUTSIDE the match so we never glue the next word.
  out = out.replace(
    /risk\s*score\s*(?:of\s*)?(\d{1,3})(\s*\/\s*100)?(?:\s*\(([^)]*?)\))?/gi,
    (_, n, slash, paren) => {
      const v = Math.max(0, Math.min(100, 100 - Number(n)));
      const t = pdiTier(Number(n));
      if (paren && /\b(BLACK|RED|ORANGE|YELLOW|GREEN)\b/i.test(paren)) {
        return `${v}/100 ${t} PDI`;
      }
      return paren ? `${v}/100 ${t} PDI (${paren})` : `${v}/100 ${t} PDI`;
    },
  );
  // Numeric "N/100 ... risk score" suffix form (e.g. "81/100 BLACK-tier risk score")
  out = out.replace(
    /(\d{1,3})\/100\s+([A-Z]+)[- ]?tier\s+risk\s+score/gi,
    (_, n, color) => {
      const v = Math.max(0, Math.min(100, 100 - Number(n)));
      const t = pdiTier(Number(n));
      const map = { BLACK: t, RED: "RED", ORANGE: "YELLOW", YELLOW: "YELLOW", GREEN: "GREEN" };
      return `${v}/100 ${map[color.toUpperCase()] || t} PDI`;
    },
  );
  // Tier-only adjectives
  out = out
    .replace(/Black[- ]?tier/gi,   tier ? `${tier} PDI tier` : "RED PDI tier")
    .replace(/Red[- ]?tier/gi,     "RED PDI tier")
    .replace(/Orange[- ]?tier/gi,  "YELLOW PDI tier")
    .replace(/Yellow[- ]?tier/gi,  "YELLOW PDI tier")
    .replace(/Green[- ]?tier/gi,   "GREEN PDI tier");

  // High-value phrases (run BEFORE the generic \brisk\b sweep)
  out = out
    .replace(/risk[- ]?adjusted/gi,        "Decision-Adjusted")
    .replace(/Procureo\s+risk\s+taxonomy/gi,"Procureo PDI taxonomy")
    .replace(/\(risk\s+[Ss]core\)/g,       "(PDI Score)")
    .replace(/\bRisk\s+Score\b/g,          "PDI Score")
    .replace(/\brisk\s+score\b/gi,         "PDI score")
    .replace(/\brisk\s+tier\b/gi,          "PDI tier")
    .replace(/\brisk\s+factors?\b/gi,      "decision factors")
    .replace(/\brisk\s+classification\b/gi,"PDI classification")
    .replace(/\brisk\s+taxonomy\b/gi,      "PDI taxonomy")
    .replace(/\brisk\s+(assessment|exposure|posture|severity|band)\b/gi, "PDI $1")
    .replace(/\bnear[- ]maximum\s+risk\b/gi, "critical PDI severity")
    .replace(/\bhigh\s+risk\b/gi,           "low PDI integrity")
    .replace(/\blow\s+risk\b/gi,            "high PDI integrity")
    .replace(/\bhighest\s+severity\s+band\b/gi, "lowest-integrity PDI band");

  // FINAL legacy-color sweep — must run AFTER "risk tier" became "PDI tier"
  out = out
    .replace(/\bBLACK\s+PDI\s+tier\b/g,  "RED PDI tier")
    .replace(/\bORANGE\s+PDI\s+tier\b/g, "YELLOW PDI tier")
    .replace(/\bBLACK\s+PDI\b/g,         "RED PDI")
    .replace(/\bORANGE\s+PDI\b/g,        "YELLOW PDI");

  // Multiplier verbiage
  if (hasScore) {
    out = out.replace(/multiplier of [\d.]+/gi,
      `${(pdiVal / 100).toFixed(2)} multiplier driven by a ${pdiVal}/100 ${tier} PDI`);
  }

  // Final fallback: remaining bare "risk" → empty (drop the word) so we never expose it
  out = out.replace(/\brisk\b\.?/gi, "");

  // Collapse double spaces / orphan punctuation
  out = out
    .replace(/\(\s*\)/g, "")
    .replace(/\s+([.,;:])/g, "$1")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
  return out;
}

// ─── Market Signal Linkage Card (WORLD ↔ SPEND fusion) ─────────────────────
const SRC_ACCENT = {
  FRED: "border-amber-700/40  bg-amber-50    text-amber-900",
  BLS:  "border-sky-700/40    bg-sky-50      text-sky-900",
  ECB:  "border-violet-700/40 bg-violet-50   text-violet-900",
};
const ALIGN_FG = { emerald: "text-emerald-800", rose: "text-rose-800", amber: "text-amber-800", slate: "text-slate-800" };
function MarketLinkageCard({ m }) {
  const d = m.driver || {};
  const s = m.secondary || {};
  const Trend = d.trend === "falling" ? TrendDown : TrendUp;
  return (
    <div
      data-testid="market-linkage-card"
      className="border border-[#D1D5DB] bg-white rounded-sm p-4"
    >
      <div className="flex items-center gap-2 mb-3">
        <GlobeHemisphereWest weight="duotone" className="text-slate-900" size={18} />
        <span className="tracking-mono-label text-[11px] text-slate-800 font-bold">
          Market Signal Linkage · {m.fusion}
        </span>
        <span className={`ml-auto font-mono text-[10px] tracking-mono-label font-bold
          ${ALIGN_FG[m.alignment_color] || "text-slate-800"}`}>
          {m.alignment}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <DriverPanel d={d} primary />
        {s && s.code && <DriverPanel d={s} />}
      </div>

      <div className="mt-1 border-l-2 border-slate-700 pl-3 py-1">
        <div className="tracking-mono-label text-[10px] text-slate-800 font-bold">
          {m.alignment_label}
        </div>
        <p className="text-sm text-slate-950 mt-1 leading-relaxed font-medium" data-testid="market-linkage-rationale">
          {humaniseText(m.rationale)}
        </p>
      </div>
      <div className="mt-2 flex items-center justify-end text-[10px] font-mono text-slate-700 font-semibold">
        <Trend size={11} className="mr-1" /> macro trend cited inline · sources : FRED · BLS · ECB
      </div>
    </div>
  );
}

function DriverPanel({ d, primary = false }) {
  const up = (d.mom_pct ?? 0) >= 0;
  const accent = SRC_ACCENT[d.source] || "border-slate-300 bg-white text-slate-900";
  return (
    <div className={`rounded-sm border ${accent} p-3`} data-testid={`linkage-driver-${d.code}`}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-mono text-[10px] tracking-mono-label font-bold">
          {primary ? "PRIMARY · " : "SECONDARY · "}{d.source} · {d.code}
        </span>
        <span className={`font-mono text-[10px] font-bold ${d.trend === "falling" ? "text-rose-800" : up ? "text-emerald-800" : "text-slate-700"}`}>
          {d.trend === "falling" ? "▼" : d.trend === "rising" ? "▲" : "·"}
          {(d.mom_pct ?? 0) >= 0 ? "+" : ""}{(d.mom_pct ?? 0).toFixed(2)}% MoM
        </span>
      </div>
      <div className="text-sm text-slate-950 mt-1 leading-snug font-medium">{d.label}</div>
      <div className="font-mono text-base text-slate-950 mt-1 font-bold">
        {d.value >= 1000 ? new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(d.value)
          : d.value >= 100 ? d.value.toFixed(1) : d.value?.toFixed(3)}
        <span className="text-[10px] text-slate-700 ml-2 font-semibold">{d.unit}</span>
      </div>
      {d.narrative && primary && (
        <p className="text-xs text-slate-800 mt-1 leading-snug font-medium">{d.narrative}</p>
      )}
    </div>
  );
}

// ─── ProofRefinementEditor ──────────────────────────────────────────────────
// Editable Partner Refinement embedded in the Strategic Proof modal.
// Lets the Strategic Lead approve-as-is or rewrite the recommendation and
// commit it straight to the Context Ledger (Mongo · partner_refinements).
function ProofRefinementEditor({
  poId,
  poNumber,
  proposedDefault,
  initialRefinement,
  committedAt,
  onCommitted,
}) {
  const seed = initialRefinement || proposedDefault || "";
  const [refined, setRefined] = useState(seed);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(committedAt || null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setRefined(initialRefinement || proposedDefault || "");
    setSavedAt(committedAt || null);
    setError(null);
  }, [initialRefinement, proposedDefault, committedAt]);

  const committedBaseline = initialRefinement || proposedDefault || "";
  const dirty = (refined || "").trim() !== (committedBaseline || "").trim();

  const resetToProposed = () => {
    setRefined(proposedDefault || "");
    setError(null);
  };

  const commitToCop = async () => {
    if (!poId || !refined?.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const r = await api.saveRefinement(poId, {
        mode: "SHOULD_COST",
        refined_strategy: refined,
        proposed_strategy: proposedDefault,
        author: "Strategic Lead",
      });
      setSavedAt(r.committed_at);
      toast.success("Strategy Synchronized", {
        description: `Decision Proof refinement committed to the COP · ${poNumber || poId}`,
        duration: 4500,
      });
      if (onCommitted) {
        onCommitted({
          po_id: poId,
          mode: "SHOULD_COST",
          refined_strategy: refined,
          proposed_strategy: proposedDefault,
          author: "Strategic Lead",
          committed_at: r.committed_at,
        });
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Sync failed";
      setError(msg);
      toast.error("Sync Failed", { description: msg, duration: 5000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="proof-refinement-editor"
      className="border border-[#E5E7EB] bg-[#F9FAFB] rounded-sm p-4"
    >
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <PencilSimple weight="duotone" className="text-slate-700" size={18} />
          <span className="font-display text-sm font-bold text-slate-800">
            Action Recommendation · Partner Refinement
          </span>
        </div>
        {dirty && (
          <span
            data-testid="proof-refinement-dirty"
            className="font-mono text-[10px] text-amber-700 tracking-mono-label"
          >
            · unsaved edits
          </span>
        )}
      </div>

      <textarea
        data-testid="proof-refinement-input"
        value={refined}
        onChange={(e) => setRefined(e.target.value)}
        rows={Math.min(12, Math.max(5, (refined?.split("\n").length || 1) + 1))}
        spellCheck={true}
        className="w-full bg-white border border-[#E5E7EB] focus:border-slate-500 outline-none
                   rounded-sm p-3 text-[13px] text-slate-800 leading-relaxed whitespace-pre-wrap resize-y"
        placeholder="Edit, delete, or rewrite the recommendation. The Strategic Lead has final authority."
      />

      <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
        <div className="font-mono text-[10px] text-slate-500">
          {savedAt && !error && (
            <span data-testid="proof-refinement-saved-stamp">
              Last committed · {new Date(savedAt).toLocaleString()}
            </span>
          )}
          {error && (
            <span className="text-rose-700" data-testid="proof-refinement-error">{error}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="proof-refinement-reset"
            onClick={resetToProposed}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-[#9CA3AF] bg-white
                       font-mono text-[11px] tracking-mono-label text-slate-800 font-semibold
                       hover:bg-[#F3F4F6] hover:border-slate-600 transition-colors"
          >
            <ArrowsClockwise size={11} weight="bold" />
            Reset to Proposed
          </button>
          <button
            data-testid="proof-refinement-commit"
            onClick={commitToCop}
            disabled={saving || !refined?.trim()}
            style={{
              backgroundColor: "#0D9488",
              color: "#FFFFFF",
              cursor: saving ? "wait" : "pointer",
              boxShadow: "0 2px 10px -2px rgba(6,95,70,0.55)",
              opacity: !refined?.trim() ? 0.6 : 1,
            }}
            onMouseOver={(e) => { if (!saving) e.currentTarget.style.backgroundColor = "#064E3B"; }}
            onMouseOut={(e) => { if (!saving) e.currentTarget.style.backgroundColor = "#0D9488"; }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-sm
                       font-display text-[12px] font-bold tracking-wide transition-colors"
            title="Commit the refinement (or approve the proposal as-is) to the COP"
          >
            <FloppyDisk size={13} weight="bold" />
            {saving ? "Syncing…" : "Commit & Sync to COP"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProofSkeleton() {
  return (
    <div className="p-6 space-y-3" data-testid="proof-loading">
      <div className="flex items-center gap-2 text-slate-700 text-xs font-mono">
        <span className="inline-block h-2 w-2 rounded-full bg-slate-700 animate-pulse" />
        Procureo Strategic Intelligence Network executing · Data Hygiene Agent → PDI Sentinel → Strategic Partner Lead
      </div>
      <div className="h-4 skeleton w-3/4 rounded-sm" />
      <div className="h-20 skeleton rounded-sm" />
      <div className="h-20 skeleton rounded-sm" />
      <div className="h-20 skeleton rounded-sm" />
    </div>
  );
}
